/**
 * Salaried Personal Loan Insert Service
 * Migrated from 3in1cms/bajajfinserv-web/ui.solid/src/lib/connectionManager/salplinsert.ts
 * Logic preserved AS-IS, updated to use modern patterns
 */

import type { ISalplInsertApiRequest, ISalplInsertApiResponse } from "../types/salplInsertApiTypes";
import { COMMON_SSO_ACCESS_TOKEN } from "../constants/sso";
import { logPageActionToNewRelic } from "@repo/shared-utils/newRelic-log-utils";
import { getLoginState, commonInsertParameters } from "@repo/shared-utils/insert-params";
import { request } from "../core/request";
import { deleteFromCartApi } from "./cart.service";
import {
  API_RESPONSE_CODES,
  OPERATION_IDS,
  SAL_NO_OFFER,
  LOAN_TYPES_MAPPING,
  V5,
} from "./constants";
import {
  getSalPLadditionalPayload,
  removeM1Fields,
  setUtmParamsForInsertConnectionManager,
  getM1TrackingParams,
  detectAndRegisterNetworkError,
} from "./insertUtils";

/**
 * Delete item from cart (internal helper)
 * Used before inserting new salaried personal loan application
 */
async function deleteFromCart(mobileNo: string, appId: string) {
  try {
    await deleteFromCartApi({ mobileNo, appId, channel: "3in1" });
  } catch (deleteError) {
    logPageActionToNewRelic("SAL_PL_DELETE_CART_ERROR", {
      component: "SAL PL Insert",
      pageUrl: typeof window !== "undefined" ? window.location.href : "",
      message: (deleteError as Error).message,
      debugInfo: JSON.stringify({
        loginState: getLoginState(),
        hasAToken: typeof window !== "undefined" && !!localStorage.getItem(COMMON_SSO_ACCESS_TOKEN),
        hasMobile: !!mobileNo,
      }),
    });
  }
}

/**
 * Insert salaried personal loan application
 *
 * @param data - Salaried personal loan application request data
 * @returns Promise with salaried personal loan insert response
 *
 * @example
 * ```typescript
 * const response = await salplInsert({
 *   mobileNo: "9876543210",
 *   journeyName: "SAL_NO_OFFER",
 *   requestedLoanAmt: "500000",
 *   loanVariant: "term"
 * });
 * ```
 */
export const salplInsert = async (
  data: ISalplInsertApiRequest
): Promise<ISalplInsertApiResponse> => {
  try {
    // Delete from cart if mobileNo and appId are provided
    if (data.mobileNo && data.appId) {
      await deleteFromCart(data.mobileNo, data.appId);
    }

    // Handle hybrid loan variant EMI amounts
    if (data.loanVariant === "hybrid") {
      const dropline = typeof data.droplineEMIAmt === "string" ? data.droplineEMIAmt : undefined;
      const hybrid = typeof data.hybridEMIAmt === "string" ? data.hybridEMIAmt : undefined;
      data.emiAmt = dropline ?? hybrid;
    }

    // Map loan variant to proper format
    if (data.loanVariant) {
      data.loanVariant = LOAN_TYPES_MAPPING[data.loanVariant] ?? data.loanVariant;
    }

    // Merge M1 tracking parameters if applicable
    const modifiedData = { ...data, ...getM1TrackingParams() };

    // Get SAL PL specific additional payload
    const additionalDetails = getSalPLadditionalPayload(modifiedData, SAL_NO_OFFER);

    // Remove M1-specific fields if not applicable
    const filteredData = removeM1Fields(modifiedData);

    // Prepare final payload with UTM parameters and save-for-later flag
    const finalPayload = {
      ...filteredData,
      ...additionalDetails,
      ...(await setUtmParamsForInsertConnectionManager()),
      saveForLaterFlag: true,
    };

    // Make API request
    const response = await request<ISalplInsertApiResponse>({
      operationId: OPERATION_IDS.salplInsert,
      requestData: {
        ...commonInsertParameters(),
        appVersion: V5,
        ...finalPayload,
      },
    });

    // Validate success status
    if (response.statusCode !== API_RESPONSE_CODES.STATUS_CODE_7001) {
      throw new Error(JSON.stringify(response));
    }

    return response;
  } catch (error) {
    // Network error detection: Register NETWORK_ERROR only if error doesn't contain statusCode
    detectAndRegisterNetworkError(error as Error, "salplInsert");

    // Log error to New Relic
    logPageActionToNewRelic("SAL_PL_INSERT_ERROR", {
      component: "SAL PL Insert",
      pageUrl: typeof window !== "undefined" ? window.location.href : "",
      message: (error as Error).message,
      debugInfo: JSON.stringify({
        loginState: getLoginState(),
        hasAToken: typeof window !== "undefined" && !!localStorage.getItem(COMMON_SSO_ACCESS_TOKEN),
        hasMobile: !!data.mobileNo,
      }),
    });

    throw error;
  }
};
